import React, { useState, useEffect, useCallback } from "react";
import {
  subscribeChartWidget,
  isChartReady,
  currentResolution,
  setResolution,
  subscribeResolutionChange,
  executeAction,
  takeScreenshot,
} from "./chartToolBridge";

// The chart's native header is disabled so this strip can sit in the app's top
// bar; these are the same intervals it used to pin as favourites.
const INTERVALS = [
  { res: "1", label: "1m" },
  { res: "5", label: "5m" },
  { res: "15", label: "15m" },
  { res: "30", label: "30m" },
  { res: "60", label: "1h" },
  { res: "240", label: "4h" },
  { res: "1D", label: "D" },
  { res: "1W", label: "W" },
  { res: "1M", label: "M" },
];

export default function ChartHeaderControls() {
  const [res, setRes] = useState("1");

  useEffect(() => {
    let unsubInterval = () => {};
    let poll = null;

    const attach = () => {
      if (!isChartReady()) return false;
      const now = currentResolution();
      if (!now) return false;
      setRes(now);
      unsubInterval();
      unsubInterval = subscribeResolutionChange((interval) => setRes(interval));
      return true;
    };

    const unsubWidget = subscribeChartWidget((w) => {
      unsubInterval();
      unsubInterval = () => {};
      if (!w) return;
      if (!attach() && !poll) {
        poll = setInterval(() => {
          if (attach()) {
            clearInterval(poll);
            poll = null;
          }
        }, 300);
      }
    });

    return () => {
      if (poll) clearInterval(poll);
      unsubInterval();
      unsubWidget();
    };
  }, []);

  const pick = useCallback(async (next) => {
    setRes(next);
    await setResolution(next);
  }, []);

  return (
    <div className="chart-head" role="toolbar" aria-label="Chart controls">
      <div className="chart-head__intervals">
        {INTERVALS.map(({ res: r, label }) => (
          <button
            key={r}
            type="button"
            className={`chart-head__tf${res === r ? " is-active" : ""}`}
            title={`${label} chart`}
            aria-pressed={res === r}
            onClick={() => pick(r)}
          >
            {label}
          </button>
        ))}
      </div>

      <span className="chart-head__sep" aria-hidden />

      <button
        type="button"
        className="chart-head__btn chart-head__btn--indicators"
        title="Indicators"
        onClick={() => executeAction("insertIndicator")}
      >
        <span className="chart-head__fx" aria-hidden>fx</span>
        <span className="chart-head__btn-text">Indicators</span>
      </button>

      <div className="chart-head__icons">
      <button
        type="button"
        className="chart-head__btn chart-head__btn--icon"
        title="Chart settings"
        aria-label="Chart settings"
        onClick={() => executeAction("chartProperties")}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
          <path
            d="M12 3v2.2M12 18.8V21M4.2 7.5l1.9 1.1M17.9 15.4l1.9 1.1M4.2 16.5l1.9-1.1M17.9 8.6l1.9-1.1"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <button
        type="button"
        className="chart-head__btn chart-head__btn--icon"
        title="Take a snapshot"
        aria-label="Take a snapshot"
        onClick={takeScreenshot}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
          <path
            d="M4 8.5h2.6L8 6.5h8l1.4 2H20v10H4z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="13" r="3" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      </button>
      </div>
    </div>
  );
}
